import { useEffect, useRef } from 'react';
import Stripe from 'stripe';

import type { ContractApiResponse } from '../../services/contract-services';
import type { OpenInvoiceApiResponse } from '../../services/payment-services';

interface TaxAddress {
    line1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
}

interface UseStripeSurchargeCalculationParams {
    isLoadingData: boolean;
    activeContracts: ContractApiResponse[];
    openInvoices: OpenInvoiceApiResponse[];
    isPayAll: boolean;
    selectedContractIds: string[];
    getTotalAmountForContract: (contractId: string) => { totalAmount: string } | undefined;
    canSelectContract: (contractId: string) => boolean;
    getContractTaxAddress: (contractId: string) => TaxAddress;
}

/**
 * Side-effect-only hook: on initial load, runs a Stripe Tax surcharge calculation for each
 * selected/payable contract. Debug/dead code preserved as-is from the original page.
 */
export const useStripeSurchargeCalculation = ({
    isLoadingData,
    activeContracts,
    openInvoices,
    isPayAll,
    selectedContractIds,
    getTotalAmountForContract,
    canSelectContract,
    getContractTaxAddress,
}: UseStripeSurchargeCalculationParams) => {
    const initialTaxCalcFetch = useRef(false);

    const createStripeTestClient = async (): Promise<Stripe | null> => {
        try {
            return new Stripe(process.env.REACT_APP_STRIPE_SECRET_KEY ?? '', { apiVersion: '2025-02-24.acacia' });
        } catch (error) {
            console.warn('Stripe setup-intent call failed:', error);
            return null;
        }
    };

    const calculateSurcharge = async (
        stripe: Stripe,
        { amount, contractId }: { amount: number; contractId: string },
    ) => {
        const address = getContractTaxAddress(contractId);
        const calculation = await stripe.tax.calculations.create({
            currency: 'usd',
            line_items: [
                {
                    amount,
                    reference: contractId,
                },
            ],
            customer_details: {
                address: {
                    line1: address.line1,
                    city: address.city,
                    state: address.state,
                    postal_code: address.postal_code,
                    country: address.country || 'US',
                },
                address_source: 'shipping',
            },
        });

        console.log('Surcharge calculation result:', contractId, calculation);

        return {
            taxAmount: calculation.tax_amount_exclusive,
            totalAmount: calculation.amount_total,
            breakdown: calculation.tax_breakdown,
            calculationId: calculation.id,
        };
    };

    useEffect(() => {
        if (initialTaxCalcFetch.current) return;
        if (isLoadingData || activeContracts.length === 0) return;
        if (!process.env.REACT_APP_STRIPE_SECRET_KEY) return;

        initialTaxCalcFetch.current = true;

        const runSurchargeCalculations = async () => {
            const stripeClient = await createStripeTestClient();
            if (!stripeClient) return;

            let contractIdsToRun = selectedContractIds;
            if (isPayAll) {
                contractIdsToRun = activeContracts
                    .map((contractItem: ContractApiResponse) => contractItem.AspireId || `Contract ${contractItem.Id}`)
                    .filter((contractId: string) => canSelectContract(contractId));
            }

            await Promise.allSettled(
                contractIdsToRun.map(async (contractId: string) => {
                    const contractData = getTotalAmountForContract(contractId);
                    const amountDollars = parseFloat(String(contractData?.totalAmount || '0'));
                    const amount = Math.round((isNaN(amountDollars) ? 0 : amountDollars) * 100);
                    if (amount <= 0) return;

                    await calculateSurcharge(stripeClient, { amount, contractId });
                }),
            );
        };

        runSurchargeCalculations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingData, activeContracts, openInvoices, isPayAll, selectedContractIds]);
};
